<script setup lang="ts">
import { customFooterClass } from '@scalar-org/css'
import {
  DocumentHeader,
  DocumentMobileHeader,
} from '@scalar-org/guide/components'
import { normalizeRefVersion } from '@scalar-org/helpers/normalize'
import {
  ApiReferenceLayout,
  SearchButton,
  SearchModal,
  useWorkspaceStoreEvents,
  type ApiReferenceConfiguration,
} from '@scalar/api-reference'
import { ScalarSidebarFooter, useModal } from '@scalar/components'
import { type OpenAPIV3_1 } from '@scalar/types/legacy'
import { useColorMode } from '@scalar/use-hooks/useColorMode'
import { createWorkspaceStore } from '@scalar/workspace-store/client'
import { emitCustomEvent } from '@scalar/workspace-store/events'
import { useHead, useSeoMeta } from '@unhead/vue'
import { useMediaQuery } from '@vueuse/core'
import { computed, useTemplateRef } from 'vue'
import { useRouter } from 'vue-router'

import ScalarFreePromo from '@/components/ScalarFreePromo.vue'
import { resolveHeaderLink } from '@/helpers/resolve-link'
import { useSidebarState } from '@/hooks'
import ReferenceDocumentSelector from '@/views/references/ReferenceDocumentSelector.vue'
import ReferenceNavWatcher from '@/views/references/ReferenceNavWatcher.vue'
import { type PublishReferenceProps } from '@/views/references/types'

const props = defineProps<
  PublishReferenceProps & {
    parsed: OpenAPIV3_1.Document
    configuration: ApiReferenceConfiguration
  }
>()

const { isDarkMode, toggleColorMode } = useColorMode()

useSeoMeta({
  title: () =>
    props.parsed.info?.title ??
    props.project.website.title ??
    'API Documentation',
  // TODO: Consider if we want to override the description
  // description: props.page.description ?? props.guide.description,
})
useHead({
  script: [
    {
      tagPosition: 'bodyClose',
      textContent: () => props.version.bodyScript,
    },
    {
      tagPosition: 'head',
      textContent: () => props.version.headScript,
    },
  ],
  style: [
    {
      tagPosition: 'head',
      textContent: () => props.version.footerCss,
    },
  ],
})

const { sidebarState, toggleSidebar, setSidebar } = useSidebarState()

const router = useRouter()

// ---------------------------------------------------------------------------

const versions = computed(() => {
  const reference = props.version.references[props.referenceUid]

  if (!reference || !reference.managedDoc) return []

  return reference.managedDoc.versions
})

const selectedVersion = computed({
  get: () => normalizeRefVersion(props.referenceVersion).version,
  set: (v) => {
    const { uid } = normalizeRefVersion(props.referenceUid, v)

    router.push({
      name: uid,
    })
  },
})

const isMobile = useMediaQuery('(max-width: 1000px)')

const footerSlot = computed(() =>
  props.version.footerBelowSidebar ? 'footer' : 'content-end',
)

const store = createWorkspaceStore()

const root = useTemplateRef<HTMLElement>('root')

useWorkspaceStoreEvents(store, root)

store.addDocument({ name: 'default', document: props.parsed })

const defaultServer = props.parsed.spec?.servers?.at(0)

/** Temporary workaround for a server selection race condition */
if (defaultServer)
  emitCustomEvent(root.value, 'scalar-update-selected-server', {
    value: defaultServer.url,
  })

const searchModalState = useModal()
</script>
<template>
  <div
    ref="root"
    class="api-references-layout">
    <ApiReferenceLayout
      class="references-sidebar-mobile-open"
      :configuration
      :isDark="isDarkMode"
      :store
      @toggleDarkMode="toggleColorMode">
      <template #header="{ breadcrumb }">
        <DocumentMobileHeader
          v-if="isMobile"
          :label="breadcrumb"
          :showSideBar="sidebarState.showSidebar"
          @showSearch="searchModalState.show()"
          @toggleSidebar="toggleSidebar" />
        <DocumentHeader
          v-else
          :activeGuide="undefined"
          :activeVersion="version.uid"
          :headerLinks="headerLinks"
          :project="project"
          :resolveInternalLinks="(item) => resolveHeaderLink(item, router)"
          :versions="versionNames"
          @navigate="(_, uid) => router.push({ name: uid })"
          @selectVersion="(uid) => router.push({ name: uid })"
          @setSidebar="setSidebar" />
        <SearchModal
          :hideModels="false"
          :modalState="searchModalState" />
        <ReferenceNavWatcher :breadcrumb />
      </template>
      <template #sidebar-start>
        <DocumentHeader
          v-if="isMobile"
          :activeGuide="undefined"
          :activeVersion="version.uid"
          :headerLinks="headerLinks"
          :project="project"
          :resolveInternalLinks="(item) => resolveHeaderLink(item, router)"
          :versions="versionNames"
          @navigate="(_, uid) => router.push({ name: uid })"
          @selectVersion="(uid) => router.push({ name: uid })"
          @setSidebar="setSidebar" />
        <div
          v-else
          class="api-references-search">
          <ReferenceDocumentSelector
            v-model="selectedVersion"
            :versions="versions" />
          <SearchButton />
        </div>
      </template>
      <template
        v-if="!isMobile"
        #sidebar-end>
        <ScalarSidebarFooter>
          <template #description>
            <ScalarFreePromo v-if="!isPaid" />
            <div v-else></div>
          </template>
        </ScalarSidebarFooter>
      </template>
      <template #[footerSlot]>
        <div
          class="references-footer"
          :class="customFooterClass"
          v-html="version.footer" />
      </template>
    </ApiReferenceLayout>
  </div>
</template>
<style scoped>
.api-references-layout {
  flex: 1;
  overflow-y: auto;
}

.api-references-search {
  padding: 12px 12px 6px 12px;
}
</style>
<style>
.scalar-card .card-form {
  --input-radius: var(--scalar-radius-lg, var(--default-theme-radius-lg));
  border-color: var(--scalar-border-color, var(--default-theme-border-color));
  color: var(--scalar-color-1, var(--default-theme-color-1));
  width: 100%;
}
.scalar-card .card-form > :first-child {
  border-top-left-radius: var(--input-radius);
  border-top-right-radius: var(--input-radius);
}
.scalar-card .card-form > :last-child {
  border-bottom-left-radius: var(--input-radius);
  border-bottom-right-radius: var(--input-radius);
}
.scalar-card .card-form > * + * {
  margin-top: -1px;
  border-top-color: transparent;
}
.scalar-card .card-form-input {
  background: transparent;
  position: relative;
  width: 100%;
  text-align: left;
  display: flex;
  border-style: solid;
  border-width: 1px;
  border-color: inherit;
}
.scalar-card .card-form-input:focus-within {
  border-color: var(--scalar-color-3, var(--default-theme-color-3));
}

.scalar-card .card-form-input label,
.scalar-card .card-form-input input {
  padding: 9px;
  border: 0;
  outline: none;
  font-size: var(--scalar-micro, var(--default-theme-micro));
  color: var(--scalar-color-2, var(--default-theme-color-2));
  width: 100%;
  background: transparent;
  appearance: none;
  -webkit-appearance: none;
  left: 0;
}
.scalar-card .card-form-input label {
  color: var(--scalar-color-1, var(--default-theme-color-1));
  width: fit-content;
  padding-right: 0;
  white-space: nowrap;
  cursor: text;
}
.scalar-card .card-form-input input {
  position: relative;
  z-index: 99;
}
.scalar-card .card-form-input input:not(:placeholder-shown) + label {
  color: var(--scalar-color-2, var(--default-theme-color-2));
}
.scalar-card .card-form-group {
  display: flex;
  border-color: inherit;
}
.scalar-card .card-form-group > * {
  border-color: inherit;
}
.scalar-card .card-form-group > :first-child {
  border-top-left-radius: inherit;
  border-bottom-left-radius: inherit;
}
.scalar-card .card-form-group > :last-child {
  border-top-right-radius: inherit;
  border-bottom-right-radius: inherit;
}
.scalar-card .card-form-group > * + * {
  margin-left: -1px;
  border-left-color: transparent;
}
.scalar-card .card-form-button {
  display: flex;
  align-items: center;
  position: relative;
  background: transparent;
  cursor: pointer;
  border-style: solid;
  border-width: 1px;
  border-color: inherit;
  padding: 9px;
  outline: none;
  white-space: nowrap;
  font-family: var(--scalar-font, var(--default-theme-font));
  font-size: var(--scalar-micro, var(--default-theme-micro));
  font-weight: var(--scalar-semibold, var(--default-theme-semibold));
  color: var(--scalar-color-1, var(--default-theme-color-1));
  height: 100%;
}
.scalar-card .card-form-button:hover {
  background: var(--scalar-background-2, var(--default-theme-background-2));
  border-color: var(--scalar-border-color, var(--default-theme-border-color));
}
.preferred-security-scheme .card-form,
.scalar-card .card-form {
  --input-radius: var(--scalar-radius-lg, var(--default-theme-radius-lg));
  border-color: var(--scalar-border-color, var(--default-theme-border-color));
  color: var(--scalar-color-1, var(--default-theme-color-1));
  width: 100%;
}
.preferred-security-scheme .card-form > :first-child,
.scalar-card .card-form > :first-child {
  border-top-left-radius: var(--input-radius);
  border-top-right-radius: var(--input-radius);
}
.preferred-security-scheme .card-form > :last-child,
.scalar-card .card-form > :last-child {
  border-bottom-left-radius: var(--input-radius);
  border-bottom-right-radius: var(--input-radius);
}
.preferred-security-scheme .card-form > * + *,
.scalar-card .card-form > * + * {
  margin-top: -1px;
  border-top-color: transparent;
}
.preferred-security-scheme .card-form-input,
.scalar-card .card-form-input {
  background: transparent;
  position: relative;
  width: 100%;
  text-align: left;
  display: flex;
  border-style: solid;
  border-width: 1px;
  border-color: inherit;
}
.preferred-security-scheme .card-form-input:focus-within,
.scalar-card .card-form-input:focus-within {
  border-color: var(--scalar-color-3, var(--default-theme-color-3));
}

.preferred-security-scheme .card-form-input label,
.scalar-card .card-form-input label,
.preferred-security-scheme .card-form-input input,
.scalar-card .card-form-input input {
  padding: 9px;
  border: 0;
  outline: none;
  font-size: var(--scalar-micro, var(--default-theme-micro));
  color: var(--scalar-color-2, var(--default-theme-color-2));
  width: 100%;
  background: transparent;
  appearance: none;
  -webkit-appearance: none;
  left: 0;
}
.preferred-security-scheme .card-form-input label,
.scalar-card .card-form-input label {
  color: var(--scalar-color-1, var(--default-theme-color-1));
  width: fit-content;
  padding-right: 0;
  white-space: nowrap;
  cursor: text;
}
.preferred-security-scheme .card-form-input input,
.scalar-card .card-form-input input {
  position: relative;
  z-index: 99;
}
.preferred-security-scheme
  .card-form-input
  input:not(:placeholder-shown)
  + label,
.scalar-card .card-form-input input:not(:placeholder-shown) + label {
  color: var(--scalar-color-2, var(--default-theme-color-2));
}
.preferred-security-scheme .card-form-group,
.scalar-card .card-form-group {
  display: flex;
  border-color: inherit;
}
.preferred-security-scheme .card-form-group > *,
.scalar-card .card-form-group > * {
  border-color: inherit;
}
.preferred-security-scheme .card-form-group > :first-child,
.scalar-card .card-form-group > :first-child {
  border-top-left-radius: inherit;
  border-bottom-left-radius: inherit;
}
.preferred-security-scheme .card-form-group > :last-child,
.scalar-card .card-form-group > :last-child {
  border-top-right-radius: inherit;
  border-bottom-right-radius: inherit;
}
.preferred-security-scheme .card-form-group > * + *,
.scalar-card .card-form-group > * + * {
  margin-left: -1px;
  border-left-color: transparent;
}
.preferred-security-scheme .card-form-button,
.scalar-card .card-form-button {
  display: flex;
  align-items: center;
  position: relative;
  background: transparent;
  cursor: pointer;
  border-style: solid;
  border-width: 1px;
  border-color: inherit;
  padding: 9px;
  outline: none;
  white-space: nowrap;
  font-family: var(--scalar-font, var(--default-theme-font));
  font-size: var(--scalar-micro, var(--default-theme-micro));
  font-weight: var(--scalar-semibold, var(--default-theme-semibold));
  color: var(--scalar-color-1, var(--default-theme-color-1));
  height: 100%;
}
.preferred-security-scheme .card-form-button:hover,
.scalar-card .card-form-button:hover {
  background: var(--scalar-background-2, var(--default-theme-background-2));
  border-color: var(--scalar-border-color, var(--default-theme-border-color));
}

.references-mobile-header {
  /* 
  Hide default mobile header in favour of
  custom guides header
  */
  display: none !important;
}
</style>
